class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
    this.loadedUrls = new Set();
    this.loadedUrls.add(this.normalizeUrl(window.location.href));
  }

  normalizeUrl(url) {
    try {
      const u = new URL(url, window.location.origin);
      u.hash = '';
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.grid = document.getElementById('product-grid');
    this.paginationContainer = document.querySelector('.pagination-wrapper');
    
    if (!this.grid || !this.paginationContainer) {
      console.log('Infinite scroll: Required elements not found', { grid: !!this.grid, pagination: !!this.paginationContainer });
      return;
    }

    this.setupObserver();
  }

  setupObserver() {
    if (this.observer) this.observer.disconnect();

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.loading) {
          this.loadNextPage();
        }
      });
    }, {
      rootMargin: '0px 0px 600px 0px'
    });

    this.observer.observe(this);
  }

  async loadNextPage() {
    if (this.loading) return;

    const allLinks = Array.from(this.paginationContainer.querySelectorAll('a[href]'));
    const nextButton = allLinks.find(a => {
      const label = (a.getAttribute('aria-label') || '').toLowerCase();
      const isNext = label.includes('next') || label.includes('siguiente');
      const isPrev = label.includes('prev') || label.includes('anterior');
      return isNext && !isPrev;
    });

    if (!nextButton || !nextButton.href || nextButton.getAttribute('aria-disabled') === 'true') {
      this.observer.disconnect();
      return;
    }

    const url = nextButton.href;
    const normalizedUrl = this.normalizeUrl(url);

    if (this.loadedUrls.has(normalizedUrl)) {
      console.log('Infinite scroll: URL already loaded, skipping', normalizedUrl);
      return;
    }

    this.loading = true;
    
    const loadingSpinner = this.querySelector('.loading__spinner');
    if (loadingSpinner) loadingSpinner.classList.remove('hidden');

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      
      const newGrid = html.getElementById('product-grid');
      const newProducts = newGrid ? newGrid.querySelectorAll('li') : [];
      const newPagination = html.querySelector('.pagination-wrapper');

      if (newProducts.length > 0) {
        this.loadedUrls.add(normalizedUrl);
        newProducts.forEach(product => {
          const importedNode = document.importNode(product, true);
          this.grid.appendChild(importedNode);
        });

        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(this.grid);
        }
        if (typeof initializeScrollZoomAnimationTrigger === 'function') {
          initializeScrollZoomAnimationTrigger();
        }

        if (typeof initVariantAddToCart === 'function') {
           initVariantAddToCart();
        }

        if (typeof initQuickAddModalToggle === 'function') {
          initQuickAddModalToggle();
        }

        const sectionId = this.grid.closest('.shopify-section')?.id?.replace('shopify-section-', '');
        const eventDetail = { sectionId: sectionId };
        
        document.dispatchEvent(new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: eventDetail
        }));

        document.dispatchEvent(new CustomEvent('theme:content:updated', {
          bubbles: true,
          detail: eventDetail
        }));

        document.dispatchEvent(new CustomEvent('collection:infinite-scroll-loaded', {
          detail: {
            url: url,
            products: newProducts
          }
        }));
      }

      if (newPagination) {
        const hasNext = Array.from(newPagination.querySelectorAll('a[href]')).find(a => {
          const label = (a.getAttribute('aria-label') || '').toLowerCase();
          return (label.includes('next') || label.includes('siguiente')) && !label.includes('prev') && !label.includes('anterior');
        });
        
        if (hasNext) {
          this.paginationContainer.innerHTML = newPagination.innerHTML;
        } else {
          this.paginationContainer.style.display = 'none';
          this.observer.disconnect();
        }
      } else {
        this.paginationContainer.style.display = 'none';
        this.observer.disconnect();
      }

    } catch (error) {
      console.error('Infinite scroll error:', error);
    } finally {
      this.loading = false;
      if (loadingSpinner) loadingSpinner.classList.add('hidden');
    }
  }
}

if (!customElements.get('infinite-scroll')) {
  customElements.define('infinite-scroll', InfiniteScroll);
}
